import React from "react";
import { AlertTriangle, Check, CircleAlert, Clock3, ExternalLink, Lightbulb, MapPin, Pencil, Phone } from "lucide-react";
import { cx } from "../../../pages/admin/_shared";
import { resolveAssetDisplayUrl } from "../../../lib/assetUrl";
import { STEP_LABEL, STEP_OF_FIELD } from "./validation";

const plain = (html) => String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const Row = ({ label, value }) => (
  <div className="flex gap-3 py-2 text-sm">
    <dt className="w-28 shrink-0 text-admin-textMuted">{label}</dt>
    <dd className="min-w-0 flex-1 break-words text-admin-text">{value || <span className="text-admin-textMuted">—</span>}</dd>
  </div>
);

/** Một nhóm tóm tắt + nút quay về bước tương ứng. */
const SummaryGroup = ({ title, step, goTo, issues = 0, children }) => (
  <section className="border-t border-admin-border pt-5">
    <div className="mb-1 flex items-center justify-between gap-3">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-admin-text">
        {title}
        {issues ? (
          <span className="rounded-full bg-admin-danger/10 px-1.5 text-[11px] font-semibold text-admin-danger">{issues} lỗi</span>
        ) : null}
      </h4>
      <button type="button" onClick={() => goTo(step)} className="inline-flex items-center gap-1 text-xs font-medium text-admin-primary hover:underline">
        <Pencil size={12} /> Sửa
      </button>
    </div>
    <dl className="divide-y divide-admin-border">{children}</dl>
  </section>
);

/** Bước cuối "Kiểm tra": trạng thái · việc còn thiếu (bấm để về đúng ô) · tóm tắt theo bước · xem trước trang. */
export default function LocationReviewStep({ form, draftMedia, categories, validation, goTo, publishAs, subtypeLabel = "", canOpenLive = false }) {
  const { errors, blockers, hints } = validation;
  const errorList = Object.entries(errors).map(([field, message]) => ({ field, message }));
  const cat = categories.find((c) => String(c._id) === String(form.category))?.name;
  const cover = draftMedia?.cover?.url ? resolveAssetDisplayUrl(draftMedia.cover.url, draftMedia.cover.key) || draftMedia.cover.url : "";
  const heroN = Array.isArray(form.heroGallery) ? form.heroGallery.length : 0;
  const blocksN = (form.mediaBlocks || []).filter((b) => String(b?.image?.url || "").trim()).length;
  const videosN = draftMedia?.videos?.length || 0;
  const address = [form.ward, form.city].filter(Boolean).join(", ");
  const hours = form.openTime || form.closeTime ? `${form.openTime || "—"} – ${form.closeTime || "—"}` : "";
  const price = String(form.priceRange || "").trim() && String(form.priceRange).trim() !== "0" ? `${form.priceRange} đ` : "";
  const ready = errorList.length === 0 && blockers.length === 0;
  const descText = plain(form.description);

  const issuesIn = (step) => errorList.filter((e) => STEP_OF_FIELD[e.field] === step).length;

  const Item = ({ tone, icon: Icon, field, message }) => (
    <li>
      <button
        type="button"
        onClick={() => goTo(STEP_OF_FIELD[field] || "basic", field)}
        className={cx("flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm transition hover:brightness-95", tone)}
      >
        <Icon size={15} className="mt-0.5 shrink-0" />
        <span className="min-w-0 flex-1">{message}</span>
        <span className="shrink-0 text-xs font-medium opacity-80">Sửa ở {STEP_LABEL[STEP_OF_FIELD[field] || "basic"]} →</span>
      </button>
    </li>
  );

  return (
    <div className="space-y-6">
      {/* Trạng thái tổng */}
      <div
        className={cx(
          "flex items-start gap-3 rounded-xl p-4",
          errorList.length ? "bg-admin-danger/[0.07]" : blockers.length ? "bg-admin-warning/10" : "bg-admin-success/10"
        )}
        role="status"
      >
        <span
          className={cx(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white",
            errorList.length ? "bg-admin-danger" : blockers.length ? "bg-admin-warning" : "bg-admin-success"
          )}
        >
          {ready ? <Check size={16} /> : <AlertTriangle size={16} />}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-admin-text">
            {errorList.length
              ? `Còn ${errorList.length} trường cần sửa trước khi lưu`
              : blockers.length
                ? `Có thể lưu nháp — còn thiếu ${blockers.length} mục để đăng`
                : publishAs === "published"
                  ? "Đủ điều kiện để xuất bản"
                  : "Sẵn sàng"}
          </p>
          <p className="mt-0.5 text-xs text-admin-textMuted">Bấm vào từng mục để quay lại đúng ô cần sửa.</p>
        </div>
      </div>

      {errorList.length || blockers.length ? (
        <ul className="space-y-1.5">
          {errorList.map((e) => (
            <Item key={`e-${e.field}`} tone="bg-admin-danger/[0.07] text-admin-danger" icon={CircleAlert} {...e} />
          ))}
          {blockers.map((b) => (
            <Item key={`b-${b.field}`} tone="bg-admin-warning/10 text-[#92400e]" icon={AlertTriangle} field={b.field} message={`Bắt buộc để xuất bản: ${b.message}`} />
          ))}
        </ul>
      ) : null}

      {hints.length ? (
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-admin-textMuted">Gợi ý để nội dung tốt hơn</p>
          <ul className="space-y-1.5">
            {hints.map((h) => (
              <Item key={`h-${h.field}`} tone="bg-admin-canvas text-admin-text" icon={Lightbulb} {...h} />
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr,320px]">
        {/* Tóm tắt theo từng bước */}
        <div className="space-y-5">
          <SummaryGroup title="Thông tin" step="basic" goTo={goTo} issues={issuesIn("basic")}>
            <Row label="Tên" value={form.name} />
            <Row label="Đường dẫn" value={form.slug ? `/location/${form.slug}` : ""} />
            <Row label="Danh mục" value={[cat, subtypeLabel].filter(Boolean).join(" · ")} />
            <Row label="Giới thiệu" value={form.shortIntro} />
            <Row label="Mô tả" value={descText ? `${descText.length} ký tự` : ""} />
            <Row label="Điện thoại" value={form.phone} />
            <Row label="Giá" value={price} />
            <Row label="Giờ mở cửa" value={hours} />
          </SummaryGroup>
          <SummaryGroup title="Media" step="media" goTo={goTo} issues={issuesIn("media")}>
            <Row label="Ảnh đại diện" value={cover ? "Đã có" : ""} />
            <Row label="Slider" value={heroN ? `${heroN} ảnh` : ""} />
            <Row label="Ảnh nội dung" value={blocksN ? `${blocksN} ảnh` : ""} />
            <Row label="Video" value={videosN ? `${videosN} video` : ""} />
          </SummaryGroup>
          <SummaryGroup title="Vị trí" step="location" goTo={goTo} issues={issuesIn("location")}>
            <Row label="Địa chỉ" value={address} />
            <Row label="Toạ độ" value={form.latitude !== "" && form.longitude !== "" ? `${form.latitude}, ${form.longitude}` : ""} />
          </SummaryGroup>
        </div>

        {/* Xem trước gần giống trang công khai */}
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-admin-text">Xem trước trên website</p>
            {canOpenLive ? (
              <a
                href={`/location/${encodeURIComponent(form.slug)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-admin-primary hover:underline"
              >
                Trang hiện tại <ExternalLink size={12} />
              </a>
            ) : null}
          </div>
          <article className="overflow-hidden rounded-xl border border-[#d9ccb4] bg-[#f7f1e6] shadow-sm">
            <div className="relative aspect-video bg-[#efe6d4]">
              {cover ? (
                <img src={cover} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center text-[#b5a78c]">
                  <MapPin size={28} />
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2.5 pt-8">
                {cat ? <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/80">{cat}</p> : null}
                <p className="line-clamp-2 font-faustina text-lg font-bold italic leading-snug text-white">{form.name || "Tên địa điểm"}</p>
              </div>
            </div>
            <div className="space-y-1.5 px-3 py-3 text-xs text-[#4a4638]">
              {address ? (
                <p className="flex items-start gap-1.5">
                  <MapPin size={12} className="mt-0.5 shrink-0" />
                  {address}
                </p>
              ) : null}
              {hours ? (
                <p className="flex items-center gap-1.5">
                  <Clock3 size={12} className="shrink-0" />
                  {hours}
                </p>
              ) : null}
              {form.phone ? (
                <p className="flex items-center gap-1.5">
                  <Phone size={12} className="shrink-0" />
                  {form.phone}
                </p>
              ) : null}
              <p className="line-clamp-4 border-t border-dashed border-[#d9ccb4] pt-2 leading-relaxed">
                {form.shortIntro || descText || <span className="italic text-[#8a7f66]">Chưa có giới thiệu</span>}
              </p>
            </div>
          </article>
          <p className="mt-2 text-xs text-admin-textMuted">Bản xem trước rút gọn — giao diện thật có thể khác đôi chút.</p>
        </div>
      </div>
    </div>
  );
}
